import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { IntentGroup } from "../../model_gen/intent";
import { Config } from "../../model_gen/config";
import { ApplicationAction } from "../../model_gen/application";
import { usePermissions } from "../../PermissionsProvider";
import { IntentService } from "../../services/IntentService";
import { TestSetService } from "../../services/TestSetService";
import { TestSet } from "../../model_gen/evaluation";
import moment from "moment";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import SentinelsTable from "../shared/SentinelsTable";
import { DatasetService } from "../../services/DatasetService";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { CompositionService } from "../../services/CompositionService";
import TestSetsTable from "../shared/TestSetsTable";
import DateRangeFilter from "../shared/DateRangeFilter";
import ModelsTable from "../shared/ModelsTable";
import CompositionsTable from "../shared/CompositionsTable";
import DatasetsTable from "../shared/DatasetsTable";
import IntentGroupIntentsTable from "./IntentGroupIntentsTable";
import { ModelService } from "../../services/ModelService";
import { SortConfig } from "../ui/paginated-table";
import { Model } from "../../model_gen/model";
import RequestsTable from "../shared/RequestsTable";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import RequestAnalyticsChart from "../shared/RequestAnalyticsChart";
import FaultAnalyticsChart from "../shared/FaultAnalyticsChart";
import {
  AnalyticsService,
  ApplicationAnalyticsResponse,
  FaultRateAnalyticsResponse,
  RequestAnalyticsData,
  FaultRateAnalyticsData,
} from "../../services/AnalyticsService";
import { QuickPeriod } from "../shared/AnalyticsDateFilter";

import {
  AppWindowIcon,
  HistoryIcon,
  FileTextIcon,
  CalendarIcon,
  ServerIcon,
  SettingsIcon,
  SearchIcon,
  PlusIcon,
  RotateCcwIcon,
  BellIcon,
  ShieldIcon,
  Loader2Icon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { ConfigurationPanelRef } from "../../types/IApplication";

dayjs.extend(utc);

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const IntentGroupOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams<{ intentGroupId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [config, setConfig] = useState<Config>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [intentsLoading, setIntentsLoading] = useState<boolean>(false);
  const [intentsTotal, setIntentsTotal] = useState(0);
  const [intentsCurrentPage, setIntentsCurrentPage] = useState(1);
  const intentsPageSize = 10;
  const intentsByPageRef = useRef<Record<number, ApplicationAction[]>>({});
  const [intentsSortConfig, setIntentsSortConfig] = useState<
    SortConfig<ApplicationAction>
  >({ key: null, direction: "desc" });
  const navigate = useNavigate();
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [activeTab, setActiveTab] = useState<string>(() => {
    return searchParams.get("tab") || "overview";
  });
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [testSetsLoading, setTestSetsLoading] = useState<boolean>(false);
  const [testSetsCurrentPage, setTestSetsCurrentPage] = useState(1);
  const testSetsPageSize = 10;
  const [testSetsTotal, setTestSetsTotal] = useState<number>(0);
  const [cachedTestSets, setCachedTestSets] = useState<
    Record<number, TestSet[]>
  >({});
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [datasetsTotal, setDatasetsTotal] = useState<number>(0);
  const [datasetsCurrentPage, setDatasetsCurrentPage] = useState<number>(1);
  const datasetsPageSize = 10;
  const [compositions, setCompositions] = useState<any[]>([]);
  const [compositionsLoading, setCompositionsLoading] =
    useState<boolean>(false);
  const [compositionsTotal, setCompositionsTotal] = useState<number>(0);
  const [compositionsCurrentPage, setCompositionsCurrentPage] =
    useState<number>(1);
  const compositionsPageSize = 10;
  const [datasetsSubTab, setDatasetsSubTab] = useState<string>("datasets");
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);
  const [modelsTotal, setModelsTotal] = useState<number>(0);
  const [modelsCurrentPage, setModelsCurrentPage] = useState<number>(1);
  const modelsPageSize = 10;

  // Configuration save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const configPanelRef = useRef<ConfigurationPanelRef>(null);

  // Analytics state
  const [requestAnalyticsData, setRequestAnalyticsData] = useState<
    RequestAnalyticsData[]
  >([]);
  const [requestAnalyticsLoading, setRequestAnalyticsLoading] =
    useState<boolean>(false);
  const [requestAnalyticsPeriod, setRequestAnalyticsPeriod] =
    useState<QuickPeriod>({ label: "1W", days: 7 });
  const [requestAnalyticsPeriodDays, setRequestAnalyticsPeriodDays] =
    useState<number>(7);

  const [faultAnalyticsData, setFaultAnalyticsData] = useState<
    FaultRateAnalyticsData[]
  >([]);
  const [faultAnalyticsLoading, setFaultAnalyticsLoading] =
    useState<boolean>(false);
  const [faultAnalyticsPeriod, setFaultAnalyticsPeriod] = useState<QuickPeriod>(
    { label: "1W", days: 7 }
  );
  const [faultAnalyticsPeriodDays, setFaultAnalyticsPeriodDays] =
    useState<number>(7);

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });

    if (!save || !intentGroup) return;

    IntentService.setIntentGroupConfig(
      Number(intentGroupId),
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setConfig(updatedConfig);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const resetConfig = () => {
    if (!intentGroup) return;

    IntentService.resetIntentGroupConfig(
      Number(intentGroupId),
      null,
      apiKey
    ).subscribe(
      () => {
        setConfig(undefined);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const handleSaveStateChange = (hasUnsaved: boolean, saving: boolean) => {
    setHasUnsavedChanges(hasUnsaved);
    setIsSaving(saving);
  };

  const handleSaveChanges = () => {
    if (configPanelRef.current) {
      configPanelRef.current.saveChanges();
    }
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    setSearchParams((params) => {
      if (tabKey === "overview") {
        params.delete("tab");
      } else {
        params.set("tab", tabKey);
      }
      return params;
    });
  };

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    IntentService.getIntentGroupRequests(
      Number(intentGroupId),
      start,
      end,
      offset,
      requestPageSize,
      setRequestsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
      }
    );
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const fetchIntents = (page: number) => {
    if (intentsByPageRef.current[page]) {
      setIntents(intentsByPageRef.current[page]);
      return;
    }

    const offset = (page - 1) * 10;
    setIntentsLoading(true);

    IntentService.getIntentsByIntentGroup(
      Number(intentGroupId),
      offset,
      10,
      setIntentsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setIntents(data.items || []);
        setIntentsTotal(data.total || 0);
        intentsByPageRef.current[page] = data.items || [];
        setIntentsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIntentsLoading(false);
      }
    );
  };

  const handleIntentsSort = (key: keyof ApplicationAction) => {
    setIntentsSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleDeleteTestSet = (testSet: TestSet) => {
    if (
      window.confirm(
        `Are you sure you want to delete test set "${testSet.name}"? This action cannot be undone.`
      )
    ) {
      TestSetService.deleteTestSet(
        testSet.id.toString(),
        null,
        apiKey
      ).subscribe(
        () => {
          const updatedTestSets = testSets.filter((ts) => ts.id !== testSet.id);
          setTestSets(updatedTestSets);
          setCachedTestSets((prev) => ({
            ...prev,
            [testSetsCurrentPage]: updatedTestSets,
          }));
          setTestSetsTotal((prev) => prev - 1);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  };

  const fetchTestSets = (page: number) => {
    if (cachedTestSets[page]) {
      setTestSets(cachedTestSets[page]);
      return;
    }

    const offset = (page - 1) * testSetsPageSize;
    setTestSetsLoading(true);
    if (intentGroupId) {
      TestSetService.getTestSetsByIntentGroup(
        intentGroupId,
        offset,
        testSetsPageSize,
        setTestSetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          setTestSets(response.items);
          setTestSetsTotal(response.total);
          setCachedTestSets((prev) => ({
            ...prev,
            [page]: response.items,
          }));
          setTestSetsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setTestSetsLoading(false);
        }
      );
    }
  };

  const fetchDatasets = (page: number) => {
    if (!intentGroupId) return;

    const offset = (page - 1) * datasetsPageSize;

    DatasetService.getDatasetsByIntentGroup(
      Number(intentGroupId),
      offset,
      datasetsPageSize,
      setDatasetsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setDatasetsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
      }
    );
  };

  const fetchCompositions = (page: number) => {
    if (!intentGroupId) return;

    const offset = (page - 1) * compositionsPageSize;

    CompositionService.getCompositionsByIntentGroup(
      Number(intentGroupId),
      offset,
      compositionsPageSize,
      setCompositionsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setCompositions(response.items || []);
        setCompositionsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching compositions:", error);
      }
    );
  };

  const fetchModels = () => {
    if (!intentGroupId) return;

    setModelsLoading(true);
    ModelService.getModelsByIntentGroup(
      Number(intentGroupId),
      setModelsLoading,
      apiKey
    ).subscribe(
      (data: Model[]) => {
        // Filter to only include finetuned models
        const finetunedModels = data.filter((model) => model.finetuned);

        // Set total count
        setModelsTotal(finetunedModels.length);

        // Client-side pagination since ModelService doesn't support server-side pagination
        const startIndex = (modelsCurrentPage - 1) * modelsPageSize;
        const endIndex = startIndex + modelsPageSize;
        const paginatedModels = finetunedModels.slice(startIndex, endIndex);

        setModels(paginatedModels);
        setModelsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setModels([]);
        setModelsTotal(0);
        setModelsLoading(false);
      }
    );
  };

  const handleDeleteDataset = (dataset: FinetuneDataset) => {
    DatasetService.deleteDataset(dataset.id, null, apiKey).subscribe(
      () => {
        fetchDatasets(datasetsCurrentPage); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting dataset:", error);
      }
    );
  };

  const handleDeleteComposition = (composition: any) => {
    CompositionService.deleteComposition(
      composition.id.toString(),
      null,
      apiKey
    ).subscribe(
      () => {
        fetchCompositions(compositionsCurrentPage); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting composition:", error);
      }
    );
  };

  // Analytics fetch functions
  const fetchRequestAnalytics = useCallback(
    (period: QuickPeriod) => {
      if (!intentGroupId) return;

      setRequestAnalyticsLoading(true);

      const now = moment();
      const days = period.days;

      let startDate: string;
      let endDate: string;

      if (days === 1) {
        // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
        startDate = now
          .clone()
          .subtract(24, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For multi-day periods, use the same logic as 1D but with more hours
        const totalHours = days * 24;
        startDate = now
          .clone()
          .subtract(totalHours, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      }

      console.log(
        `📅 Intent Group Request analytics period: ${days} days, ${startDate} to ${endDate}`
      );
      setRequestAnalyticsPeriodDays(days);

      AnalyticsService.getIntentGroupRequestAnalytics(
        Number(intentGroupId),
        setRequestAnalyticsLoading,
        apiKey,
        startDate,
        endDate
      ).subscribe(
        (response: ApplicationAnalyticsResponse) => {
          console.log("📊 Intent Group Request analytics response:", response);
          setRequestAnalyticsData(response.data || []);
        },
        (error: any) => {
          console.error(
            "Error fetching intent group request analytics:",
            error
          );
          setRequestAnalyticsData([]);
        }
      );
    },
    [intentGroupId, apiKey]
  );

  const fetchFaultAnalytics = useCallback(
    (period: QuickPeriod) => {
      if (!intentGroupId) return;

      setFaultAnalyticsLoading(true);

      const now = moment();
      const days = period.days;

      let startDate: string;
      let endDate: string;

      if (days === 1) {
        // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
        startDate = now
          .clone()
          .subtract(24, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For multi-day periods, use the same logic as 1D but with more hours
        const totalHours = days * 24;
        startDate = now
          .clone()
          .subtract(totalHours, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      }

      console.log(
        `📅 Intent Group Fault analytics period: ${days} days, ${startDate} to ${endDate}`
      );
      setFaultAnalyticsPeriodDays(days);

      AnalyticsService.getIntentGroupFaultRateAnalytics(
        Number(intentGroupId),
        setFaultAnalyticsLoading,
        apiKey,
        startDate,
        endDate
      ).subscribe(
        (response: FaultRateAnalyticsResponse) => {
          console.log("📊 Intent Group Fault analytics response:", response);
          setFaultAnalyticsData(response.data || []);
        },
        (error: any) => {
          console.error("Error fetching intent group fault analytics:", error);
          setFaultAnalyticsData([]);
        }
      );
    },
    [intentGroupId, apiKey]
  );

  const handleRequestAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setRequestAnalyticsPeriod(period);
    fetchRequestAnalytics(period);
  };

  const handleFaultAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setFaultAnalyticsPeriod(period);
    fetchFaultAnalytics(period);
  };

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        true,
        setIsLoading,
        apiKey
      ).subscribe(
        (group: IntentGroup) => {
          setIntentGroup(group);
          setConfig(group.config);
          setOriginalConfig(group.config);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (intentGroupId && activeTab === "requests") {
      fetchRequests(requestsCurrentPage);
    }
  }, [intentGroupId, requestsCurrentPage, activeTab]);

  useEffect(() => {
    if (
      (!startDate || startDate == "") &&
      (!endDate || endDate == "") &&
      activeTab === "requests"
    ) {
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "intents") {
      if (!intentsByPageRef.current[intentsCurrentPage]) {
        fetchIntents(intentsCurrentPage);
      } else {
        setIntents(intentsByPageRef.current[intentsCurrentPage]);
      }
    }
  }, [intentGroupId, intentsCurrentPage, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "testsets") {
      fetchTestSets(testSetsCurrentPage);
    }
  }, [intentGroupId, testSetsCurrentPage, activeTab]);

  useEffect(() => {
    if (intentGroupId && activeTab === "datasets") {
      if (datasetsSubTab === "datasets") {
        fetchDatasets(datasetsCurrentPage);
      } else if (datasetsSubTab === "compositions") {
        fetchCompositions(compositionsCurrentPage);
      }
    }
  }, [
    intentGroupId,
    datasetsCurrentPage,
    compositionsCurrentPage,
    activeTab,
    datasetsSubTab,
  ]);

  useEffect(() => {
    if (intentGroupId && activeTab === "models") {
      fetchModels();
    }
  }, [intentGroupId, activeTab, modelsCurrentPage]);

  // Load request analytics when overview tab becomes active
  useEffect(() => {
    if (activeTab === "overview") {
      fetchRequestAnalytics(requestAnalyticsPeriod);
    }
  }, [activeTab, fetchRequestAnalytics, requestAnalyticsPeriod]);

  // Load fault analytics when overview tab becomes active
  useEffect(() => {
    if (activeTab === "overview") {
      fetchFaultAnalytics(faultAnalyticsPeriod);
    }
  }, [activeTab, fetchFaultAnalytics, faultAnalyticsPeriod]);

  // Parse tags from metadata
  const getTags = () => {
    try {
      return JSON.parse((intentGroup?.metadata?.tags as string) || "[]");
    } catch (e) {
      return [];
    }
  };

  // Parse description from metadata
  const getDescription = () => {
    try {
      return (intentGroup?.metadata?.intent as string) || "-";
    } catch (e) {
      return "-";
    }
  };

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Total Requests
                    </p>
                  </div>
                  <p className="text-3xl font-gc-demi-bold text-foreground">
                    {intentGroup?.request_count || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <HistoryIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Last Activity
                    </p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {intentGroup?.last_activity
                      ? moment(intentGroup?.last_activity).fromNow()
                      : "Never"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Created
                    </p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {intentGroup?.date_created
                      ? moment(intentGroup.date_created).format("MMM D, YYYY")
                      : "-"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RequestAnalyticsChart
              data={requestAnalyticsData}
              loading={requestAnalyticsLoading}
              title="Request Activity"
              onQuickPeriodSelect={handleRequestAnalyticsPeriodSelect}
              selectedPeriod={requestAnalyticsPeriod}
              periodDays={requestAnalyticsPeriodDays}
            />
            <FaultAnalyticsChart
              data={faultAnalyticsData}
              loading={faultAnalyticsLoading}
              title="Fault Rate"
              onQuickPeriodSelect={handleFaultAnalyticsPeriodSelect}
              selectedPeriod={faultAnalyticsPeriod}
              periodDays={faultAnalyticsPeriodDays}
            />
          </div>
        </div>
      ),
    },
    {
      key: "intents",
      label: "Intents",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Intents</h3>
            <p className="text-sm text-muted-foreground">
              Manage intents in this group
            </p>
          </div>

          {intentsLoading && intents.length === 0 ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-4">
                  <div className="h-4 bg-muted rounded w-48 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-28 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                </div>
              ))}
            </div>
          ) : intents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="space-y-3">
                <h3 className="text-lg font-gc-med text-foreground">
                  No intents found
                </h3>
                <p className="text-muted-foreground max-w-md">
                  No intents have been created yet.
                </p>
              </div>
            </div>
          ) : (
            <IntentGroupIntentsTable
              intents={intents}
              loading={intentsLoading}
              currentPage={intentsCurrentPage}
              totalPages={Math.ceil(intentsTotal / intentsPageSize)}
              totalItems={intentsTotal}
              itemsPerPage={intentsPageSize}
              onPageChange={setIntentsCurrentPage}
              sortConfig={intentsSortConfig}
              onSort={handleIntentsSort}
            />
          )}
        </div>
      ),
    },
    {
      key: "requests",
      label: "Requests",
      icon: <SearchIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Requests</h3>
              <p className="text-sm text-muted-foreground">
                View and filter request history
              </p>
            </div>
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onClearFilters={handleClearFilters}
              onSearch={() => {
                setRequestsCurrentPage(1);
                fetchRequests(1);
              }}
              isLoading={requestsLoading}
            />
          </div>

          <RequestsTable
            entityType="intentGroup"
            entityId={Number(intentGroupId)}
            requests={requests}
            requestsTotal={requestsTotal}
            currentPage={requestsCurrentPage}
            pageSize={requestPageSize}
            loading={requestsLoading}
            onPageChange={setRequestsCurrentPage}
            onFetchRequests={fetchRequests}
            startDate={startDate}
            endDate={endDate}
            apiKey={apiKey}
            showApplicationColumn={true}
          />
        </div>
      ),
    },
    {
      key: "sentinels",
      label: "Sentinels",
      icon: <ShieldIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Sentinels</h3>
              <p className="text-sm text-muted-foreground">
                Monitor and evaluate intents
              </p>
            </div>
            <Button
              onClick={() =>
                navigate(`/intent-group/${intentGroupId}/sentinel/new`)
              }
              className="gap-2">
              <PlusIcon className="w-4 h-4" />
              New Sentinel
            </Button>
          </div>

          <SentinelsTable
            intentGroupId={Number(intentGroupId)}
            emptyText={
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="space-y-3">
                  <h3 className="text-lg font-gc-med text-foreground">
                    No sentinels found
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Create a new sentinel to monitor and evaluate intents.
                  </p>
                </div>
              </div>
            }
          />
        </div>
      ),
    },
    {
      key: "models",
      label: "Models",
      icon: <ServerIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Models</h3>
            <p className="text-sm text-muted-foreground">
              Manage models for this intent group
            </p>
          </div>

          <ModelsTable
            models={models}
            loading={modelsLoading}
            currentPage={modelsCurrentPage}
            totalPages={Math.ceil(modelsTotal / modelsPageSize)}
            totalItems={modelsTotal}
            itemsPerPage={modelsPageSize}
            onPageChange={setModelsCurrentPage}
          />
        </div>
      ),
    },
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Configuration
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage intent group settings and parameters
              </p>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
              )}
              <Button variant="outline" onClick={resetConfig} className="gap-2">
                <RotateCcwIcon className="w-4 h-4" />
                Reset to Default
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || isSaving}
                className="gap-2">
                {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>

          <ConfigurationPanel
            ref={configPanelRef}
            intentGroupId={Number(intentGroupId)}
            config={config}
            updateConfig={updateConfig}
            resetConfig={resetConfig}
            showSystemPrompt={false}
            onSaveStateChange={handleSaveStateChange}
          />
        </div>
      ),
    },
    {
      key: "testsets",
      label: "Test Sets",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Test Sets</h3>
              <p className="text-sm text-muted-foreground">
                Manage test sets for model evaluation
              </p>
            </div>
            <Button
              onClick={() =>
                navigate(`/intent-group/${intentGroupId}/testsets/new`)
              }
              className="gap-2">
              <PlusIcon className="w-4 h-4" />
              New Test Set
            </Button>
          </div>

          <TestSetsTable
            testSets={testSets}
            loading={testSetsLoading}
            currentPage={testSetsCurrentPage}
            totalPages={Math.ceil(testSetsTotal / testSetsPageSize)}
            totalItems={testSetsTotal}
            itemsPerPage={testSetsPageSize}
            onPageChange={setTestSetsCurrentPage}
            onDeleteTestSet={handleDeleteTestSet}
            intentGroupId={intentGroupId!}
          />
        </div>
      ),
    },
    {
      key: "datasets",
      label: "Datasets",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Datasets & Compositions
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage training datasets and model compositions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() =>
                  navigate(
                    `/build/compositions/new?step=1&base_type=intent_group&intent_group_id=${intentGroupId}`
                  )
                }
                className="gap-2">
                <PlusIcon className="w-4 h-4" />
                New Composition
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    `/build/datasets/new?step=1&base_type=intent_group&intent_group_id=${intentGroupId}`
                  )
                }
                className="gap-2">
                <PlusIcon className="w-4 h-4" />
                New Dataset
              </Button>
            </div>
          </div>

          {/* Sub-tabs for datasets and compositions */}
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              <button
                onClick={() => setDatasetsSubTab("datasets")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  datasetsSubTab === "datasets"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Datasets
              </button>
              <button
                onClick={() => setDatasetsSubTab("compositions")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  datasetsSubTab === "compositions"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Compositions
              </button>
            </nav>
          </div>

          {datasetsSubTab === "datasets" ? (
            <DatasetsTable
              datasets={datasets}
              loading={datasetsLoading}
              currentPage={datasetsCurrentPage}
              totalPages={Math.ceil(datasetsTotal / datasetsPageSize)}
              totalItems={datasetsTotal}
              itemsPerPage={datasetsPageSize}
              onPageChange={setDatasetsCurrentPage}
              onDeleteDataset={handleDeleteDataset}
            />
          ) : (
            <CompositionsTable
              compositions={compositions}
              loading={compositionsLoading}
              currentPage={compositionsCurrentPage}
              totalPages={Math.ceil(compositionsTotal / compositionsPageSize)}
              totalItems={compositionsTotal}
              itemsPerPage={compositionsPageSize}
              onPageChange={setCompositionsCurrentPage}
              onDeleteComposition={handleDeleteComposition}
            />
          )}
        </div>
      ),
    },
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
  }));

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {isLoading
                      ? "Loading..."
                      : intentGroup?.intent_name || "Intent Group"}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-3">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                {isLoading
                  ? "Loading..."
                  : intentGroup?.intent_name || "Intent Group"}
              </h1>
              <div className="space-y-3">
                {/* Description with AI icon */}
                <div className="flex items-start gap-1">
                  <p className="text-lg text-muted-foreground font-gc-reg">
                    {getDescription()}
                  </p>
                  <AiStars className="w-3 h-3 text-muted-foreground mt-1 flex-shrink-0" />
                </div>
                {/* Tags with AI icon */}
                <div className="flex items-start gap-1">
                  <div className="flex flex-wrap gap-2">
                    {getTags().map((tag: string) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border border-maitai-green text-maitai-green bg-green-50 dark:bg-green-950/20">
                        {tag}
                      </span>
                    ))}
                    {getTags().length === 0 && (
                      <span className="text-sm text-muted-foreground">
                        No tags available
                      </span>
                    )}
                  </div>
                  <AiStars className="w-3 h-3 text-muted-foreground mt-1 flex-shrink-0" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                      <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                      <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                      <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : (
                tabDefinitions.find((tab) => tab.key === activeTab)?.content
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntentGroupOverview;
